
import { ElementRef, inject, Renderer2, DOCUMENT } from '@angular/core';
import { EduContent, EduContentMetadataInterface, ReadSpeakerConfigService } from '@campus/dal';
import { ReadSpeakerService } from '@campus/pages/shared';
import { first, map, Observable, of, Subject } from 'rxjs';
import { LEARNOSITY_WINDOW } from '../../services';

export enum ReadSpeakerEvent {
  PLAYER_READY = 'dk:readspeaker:ready',
  PLAYER_STOPPED = 'dk:readspeaker:stopped',
}

export class ReadSpeakerConfig {
  private el: ElementRef = inject(ElementRef);
  private document: Document = inject(DOCUMENT);
  private renderer: Renderer2 = inject(Renderer2);
  private window: Window = inject(LEARNOSITY_WINDOW);
  private readSpeakerConfigService: ReadSpeakerConfigService = inject(ReadSpeakerConfigService);
  private readSpeakerService = inject(ReadSpeakerService);

  private ttsSpeakListenerDestroy: () => void;

  private readonly readSpeakerCustomerId = '13371';

  private event$ = new Subject<{ type: ReadSpeakerEvent; payload: any }>();

  public setupReadSpeakerScript(
    eduContent: EduContent,
    readspeakerTemplateId?: number
  ): Observable<{ type: ReadSpeakerEvent; payload: any }> {
    this.isReadSpeakerScriptLoaded()
      ? this.setReadspeakerConfig(eduContent, readspeakerTemplateId)
      : this.createAndAppendReadSpeakerScript(eduContent, readspeakerTemplateId);

    return this.event$.asObservable();
  }

  public startScript() {
    this.ttsSpeakListenerDestroy = this.renderer.listen(this.el.nativeElement, 'tts:speak', this.ttsSpeakHandler);
    this.overrideReadSpeakerGetPhrase();
    this.window.rsCallbacks?.readyListener();
  }

  public stopScript() {
    this.ttsSpeakListenerDestroy?.();
    this.window.ReadSpeaker?.ui?.destroyActivePlayer();

    this.event$.next({
      type: ReadSpeakerEvent.PLAYER_STOPPED,
      payload: {},
    });
    this.event$.complete();
  }

  private setReadspeakerConfig(eduContent: EduContent, readspeakerTemplateId?: number): void {
    const eduContentMetadata: EduContentMetadataInterface = eduContent?.metadata;
    const learningAreaName = eduContentMetadata?.learningArea?.name;
    const eduContentReadSpeakerConfigId = eduContentMetadata?.readSpeakerConfigId;
    const eduContentBookReadSpeakerConfigId = eduContentMetadata?.eduContentBooks?.[0]?.readSpeakerConfigId;

    const { lang, voice, voiceSpeed } = this.getReadSpeakerVoiceAndLang(learningAreaName);
    const ruleDictionary = this.getReadSpeakerDictionary(learningAreaName);
    const defaultConfig = { lang, voice, voiceSpeed, ruleDictionary };

    const configId = readspeakerTemplateId || eduContentReadSpeakerConfigId || eduContentBookReadSpeakerConfigId;
    const loadedConfig$ = configId ? this.readSpeakerConfigService.getReadSpeakerConfigById$(configId) : of(null);
    loadedConfig$
      .pipe(
        map((loadedConfig) => ({
          lang: loadedConfig?.displayedLanguage || defaultConfig.lang,
          voice: loadedConfig?.languageVoice || defaultConfig.voice,
          speed: loadedConfig?.voiceSpeed || defaultConfig.voiceSpeed,
          ruleDictionary: loadedConfig?.configSet || defaultConfig.ruleDictionary,
        })),
        first()
      )
      .subscribe(({ lang, voice, speed, ruleDictionary }) => {
        this.initReadSpeakerConfig(lang, voice, ruleDictionary, speed);
      });
  }

  private getReadSpeakerDictionary(learningAreaName: string): string {
    if (learningAreaName === 'Wiskunde') return 'default,customer,math';

    return 'default,customer';
  }

  private getReadSpeakerVoiceAndLang(learningAreaName: string): { voice: string; lang: string; voiceSpeed?: number } {
    if (learningAreaName === 'Frans') return { voice: 'Elise', lang: 'fr_fr' };
    if (learningAreaName === 'Engels') return { voice: 'Alice', lang: 'en_uk' };
    if (learningAreaName === 'Duits') return { voice: 'Max', lang: 'de_de' };

    return { voice: 'Veerle-P', lang: 'nl_be' };
  }

  private overrideReadSpeakerGetPhrase() {
    if (this.window.rspkr && this.window.rspkr.Lang && typeof this.window.rspkr.Lang.getPhrase === 'function') {
      const originalGetPhrase = this.window.rspkr.Lang.getPhrase;

      // Override getPhrase
      this.window.rspkr.Lang.getPhrase = (label: string, language: string, fallbackLanguage: string) => {
        // If language is nl_be, use nl_nl instead
        if (language === 'nl_be') language = 'nl_nl';

        return originalGetPhrase.call(this, label, language, fallbackLanguage);
      };
    }
  }

  private createAndAppendReadSpeakerScript(eduContent: EduContent, readspeakerTemplateId?: number): void {
    const script: HTMLElement = this.createReadSpeakerScript();
    if (!script) return;

    script.onload = (): void => this.setReadspeakerConfig(eduContent, readspeakerTemplateId);

    this.renderer.appendChild(this.el.nativeElement.ownerDocument.head, script);
  }

  private createReadSpeakerScript(): HTMLElement {
    const script = this.renderer.createElement('script');
    if (!script) return;

    script.id = 'readspeaker_learnosity_script';
    script.type = 'text/javascript';
    script.src = `//cdn-eu.readspeaker.com/script/${this.readSpeakerCustomerId}/webReaderForEducation/learnosity/current/ReadSpeaker.Learnosity.js`;

    return script;
  }

  private isReadSpeakerScriptLoaded(): boolean {
    return !!this.document.getElementById('readspeaker_learnosity_script');
  }

  private ttsSpeakHandler = (e: CustomEvent) => {
    const { text } = e.detail;

    this.readSpeakerService.readTextLiteral(text, {
      lang: this.window.rsConf?.Learnosity?.customerParams?.lang,
      voice: this.window.rsConf?.Learnosity?.customerParams?.voice,
      speed: this.window.rsConf?.Learnosity?.customerParams.speed,
      ruleDictionary: this.window.rsConf?.Learnosity?.customerParams?.rule,
    });
  };

  private initReadSpeakerConfig(lang: string, voice: string, ruleDictionary: string, speed?: number) {
    this.window.rsConf = {
      general: {
        cookieName: 'ReadSpeakerLearnositySettings',
        usePost: true,
        preserveElements: '.rs_preserve, .MathJax:not(.lrn_response_wrapper .MathJax)',
        uiLang: lang,
      },
      ui: {
        toolbarPosition: 'none',
        showButton: false,
        tools: {
          controlpanel: false,
          skipbuttons: false,
          speedbutton: false,
          settings: false,
          voicesettings: false,
          clicklisten: false,
          enlarge: false,
          formreading: false,
          textmode: false,
          pagemask: false,
          download: false,
          help: false,
          dictionary: false,
          translation: false,
        },
      },
      Learnosity: {
        customerParams: {
          customerid: this.readSpeakerCustomerId,
          region: 'eu',
          lang,
          voice,
          dict: ruleDictionary,
          rule: ruleDictionary,
          xslrule: ruleDictionary,
        },
        useIcons: false,
        positionIcon: null,
        addSkip: '.nav, .sr-only, button, .lrn-assess-skip-to-link, input, textarea',
        readSelector: ['.content'],
        revision: '2322',
      },
      params: `//cdn-eu.readspeaker.com/script/${this.readSpeakerCustomerId}/webReaderForEducation/learnosity/current/webReader.js?pids=wr&jit=0`,
    };

    if (speed) {
      this.window.rsConf.Learnosity.customerParams.speed = speed;
    }

    setTimeout(() => {
      // needs to emit after setup function has returned the stream
      this.event$.next({
        type: ReadSpeakerEvent.PLAYER_READY,
        payload: {},
      });
    });
  }
}
